import React, {useState} from "react"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Body} from "@irati/ui/src/Typography"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import Form from "@irati/ui/src/Form"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/recognition/recognition"
import {WalletRoutes} from "@irati-web/wallet/src/enums/Routes"
import apps from "@irati-web/app/src/routeConfig"
import {Input, Checkbox, Hidden, Select} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import Flex from "@irati/ui/src/Flex"
import {H4, H5} from "@irati/ui/src/Typography/Heading"
import {FormattedMessage, FormattedNumber, injectIntl} from "@irati/utils/lang"
import {Budget, PaymentCurrencyType, TeamState, User, WalletEntityType} from "@irati/types"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import mainTheme from "@irati/ui/src/global/theme"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {useBudgetsList, useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import {RewardRecognitionContainer, StyledFlexPointsContainer, StyledForm, TotalPointsContainer} from "./styled"
import BudgetSelect from "./BudgetSelect"
import CustomRewardSelect from "./CustomRewardSelect"

const AMOUNT_MIN_TOPUP = 337
const MIN_AMOUNT = 1
export const distributeRewardPointsSchema =
  (formatMessage: FormatMessage) =>
  (pointsInWallet: number, numberOfUsers: number, selectedTotalAmount: number, selectedBudget?: Budget) => {
    return Validation.string(formatMessage({id: "platform.validation.number.nonZeroValue"}))
      .test("Select a budget first", formatMessage({id: "recognition.reward.validation.missingBudget"}), () => !!selectedBudget)
      .test("Positive, value above 1 and no decimal", formatMessage({id: "platform.validation.number.minOne"}), (val) => val >= MIN_AMOUNT)
      .test(
        "Value cannot exceed wallet total",
        formatMessage({id: "recognition.reward.admin.distribute.validation.pointsPerUser.max"}),
        (val) => val * numberOfUsers <= pointsInWallet
      )
      .test("Insufficient funds", formatMessage({id: "recognition.reward.budget.distribute.validation.insufficientFunds"}), (val) => {
        return val * numberOfUsers <= selectedTotalAmount
      })
  }
export type RewardInfo = {
  pointsPerUser?: number
  budgetId: string | null
  useUnassigned: boolean
  customRewardId: string | null
}
type RewardTypeOption = {
  value: RewardType
  label: string
}
enum RewardType {
  CUSTOM,
  POINTS
}
interface Props extends InjectIntlProps {
  defaultSettings: RewardInfo
  onChange: (newInfo: RewardInfo) => void
  title?: string
  recipients?: User[]
  onlyRewardPoints?: boolean
}
const RewardRecognitionBanner = (props: Props) => {
  const theme = useTheme()
  const showCustomReward = authentication.apps("platform.customRewards")
  const {
    onChange,
    recipients,
    formatMessage,
    intl,
    defaultSettings,
    title = "recognition.reward.addAReward.title",
    onlyRewardPoints = false
  } = props
  const selectRewardTypeOptions: RewardTypeOption[] = [
    {
      value: RewardType.CUSTOM,
      label: intl.formatMessage({id: "recognition.reward.types.custom"})
    },
    {
      value: RewardType.POINTS,
      label: intl.formatMessage({id: "recognition.reward.types.points"})
    }
  ]
  const getDefaultSelectedRewardType = () => {
    if (!showCustomReward || onlyRewardPoints || defaultSettings.pointsPerUser) {
      return selectRewardTypeOptions.find(({value}) => RewardType.POINTS === value)
    }
    if (defaultSettings.customRewardId) {
      return selectRewardTypeOptions.find(({value}) => RewardType.CUSTOM === value)
    }
    return null
  }
  const [pointsPerUser, setPointsPerUser] = useState(defaultSettings.pointsPerUser)
  const [budgetSelected, setBudgetSelected] = useState<Budget>()
  const [selectedRewardType, setSelectedRewardType] = useState<RewardTypeOption>(getDefaultSelectedRewardType())
  const [isChecked, setIsChecked] = useState(false)
  const {data} = useBudgetsList()
  const {canBudget} = useBudgetsPermissions()
  const handleCheckUnassigned = () => onChange({...defaultSettings, useUnassigned: !defaultSettings.useUnassigned})
  const handlePointsPerUser = (newPointsPerUser: string) => {
    const newPoints = parseInt(newPointsPerUser, 10) || null
    onChange({...defaultSettings, pointsPerUser: newPoints})
    setPointsPerUser(newPoints)
  }
  const totRecipients = recipients?.length || 0
  const totalToPay = pointsPerUser ? pointsPerUser * 100 * totRecipients : 0
  const onBudgetChange = (budget: Budget) => {
    setBudgetSelected(budget)
    onChange({...defaultSettings, budgetId: budget?.uuid})
  }
  const onTopUpNowClick = () => {
    tracking.clickBuyPoints()
    tracking.clickTopupCTA()
  }
  const unassignedAmount = data?.budgets?.unassigned.amount / 100 || 0
  const isUnassignedBudget = budgetSelected?.uuid === data?.budgets?.unassigned.uuid
  const selectedBudgetAmount = budgetSelected?.amount / 100
  const showUseUnassignedCheckbox =
    canBudget &&
    !isUnassignedBudget &&
    selectedBudgetAmount + unassignedAmount >= pointsPerUser * totRecipients &&
    selectedBudgetAmount < pointsPerUser * totRecipients
  const selectedTotalAmount = isUnassignedBudget ? unassignedAmount : unassignedAmount + selectedBudgetAmount
  const onSelectRewardType = (newRewardType: RewardType) => {
    onChange({
      pointsPerUser: 0,
      budgetId: 0,
      useUnassigned: false,
      customRewardId: null
    })
    setSelectedRewardType(selectRewardTypeOptions.find(({value}) => value === newRewardType))
  }
  const onCustomRewardChange = (customRewardId: string) => {
    onChange({
      ...defaultSettings,
      customRewardId
    })
  }
  const customRewardRegions = recipients?.map((user) => {
    return user.teams?.results.find((team) => team.teamState === TeamState.BillingUnit)?.uuid
  })
  return (
    <WalletBallance
      variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
      {(apollo) => {
        if (!apollo.data?.wallet) {
          return null
        }
        const availableToGiveKey =
          apollo.data?.wallet?.availableBalance >= AMOUNT_MIN_TOPUP
            ? "recognition.reward.availableToGive"
            : "recognition.reward.availableToGiveTopup"
        return (
          <RewardRecognitionContainer>
            <H4>
              <FormattedMessage id={title} />
            </H4>
            <Flex gap={mainTheme.spacing.half} alignItems="center" marginTop={1} marginBottom={1}>
              <Icon icon={IconTypes.rewardPoints} size={16} />
              <H5 weight="bold" color={theme.colors.greys.mediumDark}>
                <FormattedMessage id={availableToGiveKey} values={{amount: intl.formatNumber(apollo.data.wallet.availableBalance!)}} />
              </H5>
              {apollo.data?.wallet?.availableBalance < AMOUNT_MIN_TOPUP && (
                <Body small>
                  <ButtonRouterLink
                    to={`${WalletRoutes.FLEXI_CHECKOUT}/top-up`}
                    app={apps.WALLET}
                    onClick={onTopUpNowClick}
                    cta
                    marginBottom={0}>
                    <FormattedMessage id="recognition.reward.cta.topUpNow" />
                  </ButtonRouterLink>
                </Body>
              )}
            </Flex>
            {showCustomReward && !onlyRewardPoints && (
              <Select
                id="selectRewardType"
                isClearable={false}
                isSearchable={false}
                options={selectRewardTypeOptions}
                hideLabel
                flush
                name="selectRewardType"
                placeholder="recognition.reward.types.placeholder"
                onChange={onSelectRewardType}
                value={selectedRewardType}
              />
            )}
            {selectedRewardType?.value === RewardType.CUSTOM && (
              <CustomRewardSelect
                onChange={onCustomRewardChange}
                defaultValue={defaultSettings.customRewardId}
                regions={customRewardRegions}
              />
            )}
            {selectedRewardType?.value === RewardType.POINTS && (
              <StyledForm>
                <BudgetSelect onBudgetChange={onBudgetChange} defaultBudgetId={defaultSettings.budgetId} />
                <StyledFlexPointsContainer>
                  <Input
                    name="pointsPerUser"
                    type="number"
                    min="1"
                    flush
                    hideLabel
                    value={String(pointsPerUser)}
                    placeholder="recognition.reward.admin.distribute.enterPoints.placeholder"
                    validation={null}
                    onChange={handlePointsPerUser}
                  />
                  <TotalPointsContainer
                    borderColor={
                      isChecked && totalToPay > apollo.data.wallet.amount ? theme.colors.tertiary.r_400 : theme.colors.utility.subdued
                    }>
                    <Body small>
                      <FormattedMessage id="recognition.reward.total.label" values={{amount: intl.formatNumber(totalToPay / 100)}} />
                    </Body>
                    <Tooltip
                      placement={OverlayPlacement.Left}
                      tooltipChildren={<FormattedMessage id="recognition.admin.reward.totalExplanation" />}>
                      <Icon icon={IconTypes.circleInformation} small />
                    </Tooltip>
                  </TotalPointsContainer>
                </StyledFlexPointsContainer>
              </StyledForm>
            )}
            {isChecked && selectedRewardType?.value === RewardType.POINTS && (
              <Hidden
                name="pointsPerUser"
                value={pointsPerUser}
                validation={
                  isChecked
                    ? distributeRewardPointsSchema(formatMessage)(
                        apollo.data.wallet.availableBalance!,
                        totRecipients,
                        selectedTotalAmount,
                        budgetSelected
                      )
                    : null
                }
              />
            )}
            {isChecked && selectedRewardType?.value === RewardType.CUSTOM && (
              <Hidden
                name="customRewardId"
                value={defaultSettings.customRewardId}
                validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
              />
            )}
            <Form>
              {showUseUnassignedCheckbox && (
                <Checkbox
                  name="check_unassigned"
                  id="checkUnassigned"
                  validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
                  checked={defaultSettings.useUnassigned}
                  onChange={handleCheckUnassigned}>
                  <Body
                    small
                    display="inline"
                    color={pointsPerUser * totRecipients - selectedBudgetAmount > unassignedAmount && theme.colors.tertiary.r_400}>
                    <FormattedMessage
                      id="platform.wallet.reward.budget.distribute.checkbox.insufficientFunds"
                      values={{
                        currentBalance: <FormattedNumber value={pointsPerUser * totRecipients - selectedBudgetAmount} />,
                        pointAllocation: <FormattedNumber value={unassignedAmount} />
                      }}
                    />
                  </Body>
                </Checkbox>
              )}
              <Checkbox
                name="rewardAccord"
                flush
                checked={isChecked}
                validation={Validation.bool().oneOf([totalToPay > 0, true], formatMessage({id: "platform.defaultValidation.required"}))}
                onChange={() => setIsChecked(!isChecked)}>
                <Body small display="inline">
                  <FormattedMessage id="recognition.reward.admin.distribute.checkbox.title" />
                </Body>
              </Checkbox>
            </Form>
            <Flex alignItems="center" gap={mainTheme.spacing.one} marginTop={1}>
              <Icon icon={IconTypes.circleInformation} small />
              <Body small>
                <FormattedMessage id="recognition.reward.onlyVisibleToRecipients.message" />
              </Body>
            </Flex>
          </RewardRecognitionContainer>
        )
      }}
    </WalletBallance>
  )
}

export default injectIntl(RewardRecognitionBanner)
